import { HttpClientInterface, HttpRequestOptions } from '@/interfaces/api/http-client.interface'

import { HttpError } from '@/exceptions/http.error'
import { HttpMethodType } from '@/types/http-method.type'

abstract class HttpClientService implements HttpClientInterface {
  protected readonly apiBaseUrl

  constructor() {
    this.apiBaseUrl = process.env.VUE_APP_API_BASE_URL
  }

  public delete<T>(url: string, options: HttpRequestOptions = {}): Promise<T> {
    return fetch(this.generateApiUrl(url), {
      ...HttpClientService.convertRequestOptions(options),
      method: HttpMethodType.DELETE,
    })
      .then((res) => HttpClientService.handleResponse<T>(res))
      .catch(HttpClientService.handleError)
  }

  public get<T>(url: string, options: HttpRequestOptions = {}): Promise<T> {
    return fetch(this.generateApiUrl(url), {
      ...HttpClientService.convertRequestOptions(options),
      method: HttpMethodType.GET,
    })
      .then((res) => HttpClientService.handleResponse<T>(res))
      .catch(HttpClientService.handleError)
  }

  public post<T>(url: string, options: HttpRequestOptions = {}): Promise<T> {
    return fetch(this.generateApiUrl(url), {
      ...HttpClientService.convertRequestOptions(options),
      headers: {
        ...(options.headers || {}),
        'Content-Type': 'application/json',
      },
      method: HttpMethodType.POST,
    })
      .then((res) => HttpClientService.handleResponse<T>(res))
      .catch(HttpClientService.handleError)
  }

  public put<T>(url: string, options: HttpRequestOptions = {}): Promise<T> {
    return fetch(this.generateApiUrl(url), {
      ...HttpClientService.convertRequestOptions(options),
      headers: {
        ...(options.headers || {}),
        'Content-Type': 'application/json',
      },
      method: HttpMethodType.PUT,
    })
      .then((res) => HttpClientService.handleResponse<T>(res))
      .catch(HttpClientService.handleError)
  }

  protected generateApiUrl(url: string): string {
    return `${this.apiBaseUrl}/${url}`
  }

  protected static convertRequestOptions(options: HttpRequestOptions): RequestInit {
    return {
      body: JSON.stringify(options.body),
      headers: options.headers,
    }
  }

  protected static handleResponse<T>(res: Response): T | PromiseLike<T> {
    if (!res.ok) {
      throw new HttpError(res.statusText)
    }
    const response = res.status === 204 ? null : res.json()
    return response as Promise<T>
  }

  protected static handleError(err: Error): PromiseLike<never> {
    throw new HttpError(err.message)
  }
}

export { HttpClientService }
